/*
 * Fishing minigame — 45 seconds. Player is anchored next to the well; a small
 * state machine cycles through casting → waiting → nibble → (caught | escaped).
 * Tap during the nibble window scores; earlier tap = 2 fish, late = 1 fish.
 */

import type { FishState } from '@/state/types';
import { save, persist } from '@/state/save';

export const FISH_DURATION_SEC = 45;
export const NIBBLE_WINDOW_SEC = 1.4;

export function createFishState(): FishState {
  return {
    active: false,
    state: 'idle',
    stateTime: 0,
    nibbleAt: 0,
    nibbleWindow: 0,
    score: 0,
    timeLeft: 0,
  };
}

export function startFish(state: FishState): void {
  state.active = true;
  state.state = 'casting';
  state.stateTime = 0;
  state.score = 0;
  state.timeLeft = FISH_DURATION_SEC;
}

export interface FishTickEvents {
  /** Called when the nibble starts and the on-screen prompt should glow. */
  onNibbleStart: () => void;
  /** Called when the nibble window ends without a tap (a miss). */
  onNibbleMiss: () => void;
  /** Called when the timer reaches 0. */
  onEnd: (score: number) => void;
}

/** Tick fishing FSM by dt seconds. */
export function tickFish(state: FishState, dt: number, events: FishTickEvents): void {
  if (!state.active) return;
  state.timeLeft -= dt;
  state.stateTime += dt;

  if (state.state === 'casting' && state.stateTime > 0.8) {
    state.state = 'waiting';
    state.stateTime = 0;
    state.nibbleAt = 2 + Math.random() * 4;
  } else if (state.state === 'waiting' && state.stateTime > state.nibbleAt) {
    state.state = 'nibble';
    state.stateTime = 0;
    state.nibbleWindow = NIBBLE_WINDOW_SEC;
    events.onNibbleStart();
  } else if (state.state === 'nibble') {
    state.nibbleWindow -= dt;
    if (state.nibbleWindow <= 0) {
      state.state = 'casting';
      state.stateTime = 0;
      events.onNibbleMiss();
    }
  }

  if (state.timeLeft <= 0) {
    endFish(state, events);
  }
}

/** Player tapped during the nibble window. Returns the points scored (1 or 2). */
export function handleNibbleTap(state: FishState): number {
  if (!state.active || state.state !== 'nibble') return 0;
  const elapsed = NIBBLE_WINDOW_SEC - state.nibbleWindow;
  const quality = 1 - elapsed / NIBBLE_WINDOW_SEC;
  const value = quality > 0.65 ? 2 : 1;
  state.score += value;
  state.state = 'casting';
  state.stateTime = 0;
  return value;
}

export function endFish(state: FishState, events: FishTickEvents): void {
  if (!state.active) return;
  state.active = false;
  state.state = 'idle';
  if (state.score > save.bestFish) save.bestFish = state.score;
  void persist();
  events.onEnd(state.score);
}
